import React, { useMemo } from 'react';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import NukaCarousel from '@r1-frontend/ui-react/components/NukaCarousel';
import { truncate } from '@r1-frontend/shared/helpers/truncate';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import type { IBanner } from '~/src/api/content/banners';
import PromoBanner from '~/src/features/index/Banners/PromoBannersSection/PromoBanner';

import * as ST from './styled';

const DEFAULT_BANNERS_COUNT = 1;
const MAX_TITLE_LENGTH = 35;
const MAX_DESCRIPTION_LENGTH = 90;

interface IProps {
    banners: IBanner[],
    withContainer?: boolean,
    withEllipsis?: boolean,
    className?: string,
}

const PromoBannersSection: React.FC<IProps> = ({ banners = [], withEllipsis = true, className = '' }): JSX.Element => {
    const isDoubleBanner = banners.length > DEFAULT_BANNERS_COUNT;
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const renderBannersList = useMemo(() => {
        return banners?.map(({ bannerInfo }): JSX.Element => {
            const titleText = truncate(bannerInfo.title, MAX_TITLE_LENGTH);
            const descText = truncate(bannerInfo.text, MAX_DESCRIPTION_LENGTH);

            return (
                <ST.BannerCol
                    key={bannerInfo.id}
                    xs={12}
                    isDoubleBanner={isDoubleBanner}>
                    <PromoBanner
                        bannerIdx={bannerInfo.id}
                        title={withEllipsis ? titleText : bannerInfo.title}
                        description={withEllipsis ? descText : bannerInfo.text}
                        background={bannerInfo.image_background}
                        backgroundWebp={bannerInfo.image_background_webp}
                        image={bannerInfo.image}
                        imageWebp={bannerInfo.image_webp}
                        textColor={bannerInfo.text_color}
                        buttonText={bannerInfo.button_text}
                        buttonType={bannerInfo.button_type}
                        tags={bannerInfo.tags}
                        route={bannerInfo.link}
                        legal={{
                            ageLimit: bannerInfo.age_limit,
                            text: bannerInfo.legal_info,
                            textColor: bannerInfo.legal_info_text_color,
                        }}
                        data-test='promo-banner'
                        advertising={bannerInfo.advertising}
                    />
                </ST.BannerCol>
            );
        });
    }, [banners, isDoubleBanner]);

    return (
        <ST.Wrapper className={className}>
            <ST.BannerRow notSingle={banners?.length > 1}>
                {isMobile && isDoubleBanner
                    ? <NukaCarousel
                        withoutArrows={true}
                        cellSpacing={16}
                    >
                        {renderBannersList}
                    </NukaCarousel>
                    : renderBannersList
                }
            </ST.BannerRow>
        </ST.Wrapper>
    );
};

export default PromoBannersSection;
